import { supabase } from "../supabase";
import trimSentence from "../utils/trimSentence";
import matchBook from "../utils/matchBook";

/**
 * 앱 전역에서 쓰는 "책" 모양은 Firestore 시절과 동일하게 유지한다.
 * 화면 코드를 최소한만 고치기 위해, 정규화된 테이블을 읽어와 예전 모양으로 다시 접는다.
 *
 *   { id, title, author, genre, cover, readers,
 *     registeredBy: {uid: "2024.01.01"}, completedBy: {...},
 *     recommendedBy: {uid: true}, emotions: {uid: {bright, calm}},
 *     memos: [{ id, userId, text, isPublic, location, createdAt, likes: [], replies: [] }] }
 */

const toDisplay = (d) => (d ? String(d).replaceAll("-", ".") : "");

const BOOK_SELECT = `
  id, title, author, genre, cover, description, isbn,
  user_books ( user_id, registered_at, completed_at, recommended, want_to_read, created_at ),
  book_emotions ( user_id, bright, calm ),
  memos (
    id, user_id, text, is_public, location, created_at,
    memo_replies ( id, user_id, text, created_at ),
    memo_likes ( user_id )
  )
`;

function foldBook(row) {
  const registeredBy = {};
  const completedBy = {};
  const recommendedBy = {};
  const wantToReadBy = {};
  for (const ub of row.user_books ?? []) {
    registeredBy[ub.user_id] = toDisplay(ub.registered_at);
    if (ub.completed_at) completedBy[ub.user_id] = toDisplay(ub.completed_at);
    if (ub.recommended) recommendedBy[ub.user_id] = true;
    if (ub.want_to_read) wantToReadBy[ub.user_id] = true;
  }

  const emotions = {};
  for (const e of row.book_emotions ?? []) {
    emotions[e.user_id] = { bright: e.bright, calm: e.calm };
  }

  const memos = (row.memos ?? [])
    .map((m) => ({
      id: m.id,
      userId: m.user_id,
      text: m.text,
      isPublic: m.is_public,
      location: m.location ?? undefined,
      createdAt: toDisplay(String(m.created_at).slice(0, 10)),
      createdAtRaw: m.created_at,
      likes: (m.memo_likes ?? []).map((l) => l.user_id),
      replies: (m.memo_replies ?? [])
        .map((r) => ({
          id: r.id,
          userId: r.user_id,
          text: r.text,
          createdAt: toDisplay(String(r.created_at).slice(0, 10)),
          createdAtRaw: r.created_at,
        }))
        .sort((a, b) => a.createdAtRaw.localeCompare(b.createdAtRaw)),
    }))
    .sort((a, b) => b.createdAtRaw.localeCompare(a.createdAtRaw));

  // 책이 MUROO 에 처음 등록된 날. Firestore 시절 books.registeredDate 를 대체한다.
  // '최근 7일' 배지와 미완독 화면의 날짜 표시가 이 값을 쓴다.
  const firstRegistered = (row.user_books ?? [])
    .map((ub) => ub.registered_at)
    .filter(Boolean)
    .sort()[0];

  return {
    id: row.id,
    title: row.title,
    author: row.author,
    registeredDate: toDisplay(firstRegistered),
    // 누군가 실제로 서재에 담은 가장 최근 시각. DB 가 찍으므로 못 고친다.
    //
    // registeredDate 는 사용자가 적는 '읽기 시작한 날' 이라 쓸 수 없고,
    // books.created_at 도 못 쓴다 — 마이그레이션 때 114권이 모두 새로
    // 만들어져 전부 '최근' 이 된다(8/31 21시에 43권).
    //
    // 이전된 행은 null 이다. 언제 담겼는지 알 수 없으니 새 책이라고
    // 주장하지 않는다. 앞으로 담는 것부터 세진다.
    addedDate: (() => {
      const stamps = (row.user_books ?? []).map((ub) => ub.created_at).filter(Boolean);
      if (!stamps.length) return "";
      return toDisplay(String(stamps.sort().at(-1)).slice(0, 10));
    })(),
    genre: row.genre,
    cover: row.cover,
    description: row.description,
    // 책별 공개 페이지(muroo.kr/book/<isbn>) 주소를 만드는 데 쓴다.
    isbn: row.isbn ?? null,
    // 담아두기만 한 사람은 독자가 아니다.
    readers: (row.user_books ?? []).filter((ub) => !ub.want_to_read).length,
    registeredBy,
    completedBy,
    wantToReadBy,
    recommendedBy,
    emotions,
    memos,
  };
}

export async function listBooks() {
  const { data, error } = await supabase.from("books").select(BOOK_SELECT);
  if (error) throw error;
  return data.map(foldBook);
}

export async function getBook(bookId) {
  const { data, error } = await supabase
    .from("books")
    .select(BOOK_SELECT)
    .eq("id", bookId)
    .maybeSingle();
  if (error) throw error;
  return data ? foldBook(data) : null;
}

/**
 * ISBN 으로 책 한 권. 검색으로 들어온 사람을 맞이하는 데 쓴다.
 *
 * 로그인 전에 부른다 — books 는 누구나 읽을 수 있게 열려 있다(RLS books_read).
 * 없는 ISBN 이면 null 이고, 그때는 평소 로그인 화면이 그대로 나온다.
 */
export async function findBookByIsbn(isbn) {
  if (!/^97[89]\d{10}$/.test(String(isbn ?? ""))) return null;
  const { data, error } = await supabase
    .from("books")
    .select(BOOK_SELECT)
    .eq("isbn", isbn)
    .maybeSingle();
  if (error) return null;
  return data ? foldBook(data) : null;
}

/** AddModal / BookHero 의 "title+author 로 이미 있나?" 조회를 대체 */
export async function findBook(title, author) {
  const { data, error } = await supabase
    .from("books")
    .select(BOOK_SELECT)
    .eq("title", title.trim())
    .eq("author", author.trim())
    .maybeSingle();
  if (error) throw error;
  return data ? foldBook(data) : null;
}

/**
 * 13자리 ISBN 만 골라낸다.
 *
 * 카카오는 "8936434128 9788936434120" 처럼 10자리와 13자리를 한 칸 띄워
 * 함께 준다. 도서관 정보나루·국립중앙도서관 API 는 13자리로 책을 찾으므로
 * 그것만 남긴다. 다듬는 일을 부르는 쪽에 맡기면 경로가 넷이라 언젠가 한
 * 곳이 빠진다 — 저장하는 여기 한 곳에서 한다.
 */
export function isbn13(raw) {
  const hit = String(raw ?? "").split(/\s+/).find((v) => /^97[89]\d{10}$/.test(v));
  return hit || null;
}

/**
 * 책을 카탈로그에 넣고(있으면 재사용) 내 서재에 등록한다.
 * Firestore 시절엔 "조회 → 없으면 생성 → readers 증가 → users.bookIds 추가" 4단계였고
 * 그 사이에 경쟁 조건이 있었다. 이제 unique 제약과 upsert 가 처리한다.
 */
export async function registerBook({ title, author, genre = null, cover = null, description = null, isbn = null }, userId, { wantToRead = false } = {}) {
  // upsert 는 넘긴 컬럼을 그대로 UPDATE 한다. description 을 늘 넣으면
  // 소개문 없이 담는 경로(BookHero)가 이미 저장된 소개문을 null 로 지운다.
  // 값이 있을 때만 키를 넣어 기존 값을 건드리지 않는다.
  const payload = { title: title.trim(), author: (author ?? "").trim(), genre, cover };
  if (description) payload.description = description;
  // isbn 도 같은 이유로 값이 있을 때만 넣는다. 소개문 없이 담는 경로가
  // 이미 채워둔 ISBN 을 지우면 대출 통계가 조용히 사라진다.
  const isbnValue = isbn13(isbn);
  if (isbnValue) payload.isbn = isbnValue;

  const { data: book, error: bookErr } = await supabase
    .from("books")
    .upsert(payload, { onConflict: "title,author", ignoreDuplicates: false })
    .select("id, title, author, genre, cover, description, created_at")
    .single();
  if (bookErr) throw bookErr;

  // upsert 는 새로 넣든 기존 걸 덮어쓰든 똑같이 한 줄을 돌려준다.
  // created_at 만이 둘을 구분한다 — 기존 책이면 예전 시각 그대로다.
  const isNewToMuroo = Date.now() - new Date(book.created_at).getTime() < 30_000;

  /* 이미 담아둔 책인지 먼저 본다. upsert 뒤의 .select() 로 판단하면
     ignoreDuplicates 일 때 PostgREST 가 무엇을 돌려주는지에 기대게 되고,
     그 기대가 틀리면 알림이 조용히 안 나간다 — 오늘 삭제에서 0건을
     성공으로 넘기던 것과 같은 모양이다. 한 번 더 물어보고 확실히 안다. */
  const { data: existing } = await supabase
    .from("user_books")
    .select("book_id")
    .eq("user_id", userId)
    .eq("book_id", book.id)
    .maybeSingle();
  const alreadyMine = !!existing;

  const { error: ubErr } = await supabase
    .from("user_books")
    .upsert(
      {
        user_id: userId,
        book_id: book.id,
        registered_at: new Date().toISOString().slice(0, 10),
        want_to_read: wantToRead,
      },
      // ignoreDuplicates 라 이미 담은 책은 건드리지 않는다. 읽는 중인 책을
      // '읽고 싶은 책' 으로 되돌리지 않기 위해서다.
      { onConflict: "user_id,book_id", ignoreDuplicates: true }
    );
  if (ubErr) throw ubErr;

  // 정말 새로 담았을 때만 알린다. 이미 갖고 있던 책에도 알리면 같은 책을
  // 다시 눌러볼 때마다 남에게 알림이 간다.
  if (!alreadyMine) await notifySameBook(book.id, book.title, userId);

  return { ...book, isNewToMuroo };
}

/**
 * 같은 책을 이미 담아 둔 사람들에게 알린다.
 *
 * 이 앱에서 사람을 만나는 길은 겹치는 책이다. 누가 내 책을 담았다는 것이
 * 그 길이 열렸다는 신호다.
 *
 * 알림이 실패해도 책은 이미 담겼다. 던지지 않는다.
 * 스무 명에서 끊는다 — 더 많은 사람이 가진 책이면 알림이 아니라 소음이다.
 */
async function notifySameBook(bookId, bookTitle, userId) {
  try {
    const { data: owners } = await supabase
      .from("user_books")
      .select("user_id")
      .eq("book_id", bookId)
      .neq("user_id", userId)
      .limit(20);
    if (!owners || owners.length === 0) return;
    await supabase.from("notifications").insert(
      owners.map((o) => ({
        user_id: o.user_id,
        from_user_id: userId,
        type: "same_book",
        book_title: bookTitle,
      }))
    );
  } catch (e) {
    console.error("같은 책 알림 실패:", e.message);
  }
}

/** 읽고 싶은 책을 펴는 순간. registered_at 은 '읽기 시작한 날' 이므로
 *  담아둔 날이 아니라 오늘로 다시 잡는다. */
export async function startReading(bookId, userId) {
  const { error } = await supabase
    .from("user_books")
    .update({ want_to_read: false, registered_at: new Date().toISOString().slice(0, 10) })
    .eq("user_id", userId)
    .eq("book_id", bookId);
  if (error) throw error;
}

/** 다시 읽고 싶은 책으로 되돌린다. 잘못 눌렀을 때 빠져나갈 길이 필요하다. */
export async function setWantToRead(bookId, userId, want) {
  const { error } = await supabase
    .from("user_books")
    .update({ want_to_read: want })
    .eq("user_id", userId)
    .eq("book_id", bookId);
  if (error) throw error;
}

/** 내 서재에서 제거. 아무도 안 가진 책은 DB 트리거가 알아서 지운다. */
export async function unregisterBook(bookId, userId) {
  const { data, error } = await supabase
    .from("user_books")
    .delete()
    .eq("user_id", userId)
    .eq("book_id", bookId)
    .select("book_id");
  if (error) throw error;
  /* 0건 삭제는 오류가 아니다. RLS 로 막혀도, 조건이 아무 행에도 안 맞아도
     error 는 null 로 온다. 그대로 성공 처리하면 화면에서만 사라지고
     실시간 갱신 때 책이 되돌아온다 — '삭제가 안 된다' 의 모양이다. */
  if (!data || data.length === 0) {
    throw new Error("지울 기록을 찾지 못했어요 (0건)");
  }
}

export async function setCompleted(bookId, userId, dateStr /* "2024.01.01" | null */) {
  const { error } = await supabase
    .from("user_books")
    .update({ completed_at: dateStr ? dateStr.replaceAll(".", "-") : null })
    .eq("user_id", userId)
    .eq("book_id", bookId);
  if (error) throw error;
}

export async function setRegisteredDate(bookId, userId, dateStr) {
  const { error } = await supabase
    .from("user_books")
    .update({ registered_at: dateStr.replaceAll(".", "-") })
    .eq("user_id", userId)
    .eq("book_id", bookId);
  if (error) throw error;
}

export async function setRecommended(bookId, userId, recommended) {
  const { error } = await supabase
    .from("user_books")
    .update({ recommended })
    .eq("user_id", userId)
    .eq("book_id", bookId);
  if (error) throw error;
}

export async function setGenre(bookId, genre) {
  const { error } = await supabase.from("books").update({ genre }).eq("id", bookId);
  if (error) throw error;
}

export async function setCover(bookId, cover) {
  const { error } = await supabase.from("books").update({ cover }).eq("id", bookId);
  if (error) throw error;
}

export async function setEmotion(bookId, userId, bright, calm) {
  const { error } = await supabase
    .from("book_emotions")
    .upsert({ user_id: userId, book_id: bookId, bright, calm }, { onConflict: "user_id,book_id" });
  if (error) throw error;
}

/** onSnapshot(collection(db,"books")) 대체 */
export function subscribeBooks(onChange) {
  const channel = supabase
    .channel("books-changes")
    .on("postgres_changes", { event: "*", schema: "public", table: "books" }, onChange)
    .on("postgres_changes", { event: "*", schema: "public", table: "user_books" }, onChange)
    .on("postgres_changes", { event: "*", schema: "public", table: "memos" }, onChange)
    .subscribe();
  return () => supabase.removeChannel(channel);
}

/** 기념할 권수. 늘리려면 여기에 숫자만 더한다. */
export const MILESTONES = [111];

/**
 * 방금 등록한 책이 MUROO 의 111번째 책인지 확인하고, 맞다면 자리를 잡는다.
 *
 * 권수만 세면 두 사람이 동시에 111을 볼 수 있다. insert 가 성공한
 * 한 사람만 진짜다 — 나머지는 기본키 충돌(23505)로 조용히 물러난다.
 * 판정을 DB 에 맡기는 이유다.
 *
 * @returns {Promise<{n:number, book:object}|null>} 잡았으면 그 순간, 아니면 null
 */
export async function claimMilestone(book, userId) {
  if (!book?.isNewToMuroo || !userId) return null;

  const { count, error: countErr } = await supabase
    .from("books")
    .select("id", { count: "exact", head: true });
  if (countErr || !MILESTONES.includes(count)) return null;

  const { error } = await supabase
    .from("milestones")
    .insert({ n: count, book_id: book.id, user_id: userId });
  // 23505 = 이미 누가 잡았다. 실패해도 책 등록 자체는 멀쩡하니 조용히 넘어간다.
  if (error) return null;

  return { n: count, book };
}

/**
 * 소개문이 비어 있는 책을 열었을 때 한 번 채워 넣는다.
 *
 * 등록 때는 검색 응답에 이미 있던 contents 를 쓰므로 공짜지만,
 * Open Library 까지 내려간 책이나 contents 가 빈 책은 null 로 남는다.
 * 그런 책을 실제로 열어본 시점에만 한 번 더 찾는다 — 등록 흐름은
 * 그대로 두고, 아무도 안 여는 책에는 비용을 쓰지 않는다.
 *
 * 찾지 못하면 빈 문자열을 넣는다. null 로 두면 열 때마다 다시
 * 찾으면서 매번 실패한다 (『블리치』는 다른 판본만 잡힌다).
 *   null → 아직 안 해봄 · '' → 해봤는데 없었음
 *
 * @returns {Promise<string|null>} 저장한 소개문. 못 찾았으면 "" , 시도 안 했으면 null
 */
export async function fetchDescription(book) {
  if (!book?.id || book.description != null) return null;

  let description = "";
  try {
    const { data } = await supabase.functions.invoke("search-books", {
      body: { q: `${book.title} ${book.author}` },
    });
    const hit = matchBook(book, data?.documents);
    if (hit?.contents) description = trimSentence(hit.contents);
  } catch {
    // 검색이 실패한 것과 소개문이 없는 것은 다르다.
    // 여기서 '' 를 저장하면 다시는 안 찾으므로 null 인 채로 물러난다.
    return null;
  }

  // is("description", null) 로 먼저 채운 쪽을 이기게 둔다.
  // 두 사람이 같은 책을 동시에 열어도 값이 뒤집히지 않는다.
  const { error } = await supabase
    .from("books")
    .update({ description })
    .eq("id", book.id)
    .is("description", null);
  if (error) return null;

  return description;
}
